import React, { createContext, useContext } from "react";
import { DEFAULT_FONT } from "repoV2/features/Common/constants/styles";
import { checkIsObjectEmpty } from "repoV2/utils/common/dataTypes/object";
import { ICreator } from "repoV2/features/CreatorDetails/CreatorDetails.interfaces";
import { ICheckoutCreatorDetails } from "../utils/apis/CreatorDetailsApi/ICreatorDetailsApi.interface";

interface ICheckoutThemeContextValue {
    colors: {
        color1: string;
        color2: string;
        color3: string;
        color4: string;
    };
    font: ICreator.IThemeFont;
}

interface ICheckoutThemeContextProviderProps {
    creatorDetails: ICheckoutCreatorDetails;
    dynamicLinkUuid: string;
    children?: JSX.Element | JSX.Element[];
}

const initial_context: ICheckoutThemeContextValue = {
    font: DEFAULT_FONT,
    colors: {
        color1: "#009ddd", // '009ddd' is the default color, incase we don't have any in the host theme
        color2: "#1c274c",
        color3: "#625f72",
        color4: "#fafafa",
    },
};

const CheckoutThemeContext = createContext(initial_context);

const CheckoutThemeContextProvider: React.FC<ICheckoutThemeContextProviderProps> =
    props => {
        const { creatorDetails, dynamicLinkUuid, children } = props;
        const {
            theme,
            colors: customThemeColors = {},
            font = DEFAULT_FONT,
            payment_link_appearance: paymentLinkappearance = {},
        } = creatorDetails;
        const { color_codes: defaultThemeColors = {} } = theme || {};

        // If, there is color from theme, then we pick it up
        // else we set the colot from 'colors'
        const hostThemeColor1 =
            customThemeColors["template-primary-color"] ||
            defaultThemeColors["template-primary-color"];
        let colors = {
            ...initial_context.colors,
            color1: hostThemeColor1
                ? `rgb(${hostThemeColor1})`
                : initial_context.colors.color1,
        };

        const { color_pallete: customPaymentPageColorPalette = {} } =
            paymentLinkappearance;

        if (
            dynamicLinkUuid &&
            !checkIsObjectEmpty(customPaymentPageColorPalette)
        ) {
            colors = {
                ...colors,
                ...(customPaymentPageColorPalette.theme_color_1 && {
                    color1: customPaymentPageColorPalette.theme_color_1,
                }),
                ...(customPaymentPageColorPalette.theme_color_2 && {
                    color2: customPaymentPageColorPalette.theme_color_2,
                }),
                ...(customPaymentPageColorPalette.theme_color_3 && {
                    color3: customPaymentPageColorPalette.theme_color_3,
                }),
                ...(customPaymentPageColorPalette.theme_color_4 && {
                    color4: customPaymentPageColorPalette.theme_color_4,
                }),
            };
        }
        // TODO: @mayank custom color codes values can be processed here once provided by backend
        // const colorPallete = data.colorPallete;
        // const colors = processColorPalleteToColors(colorPallete);

        const overallTheme = { colors, font };

        return (
            <CheckoutThemeContext.Provider value={overallTheme}>
                {children}
            </CheckoutThemeContext.Provider>
        );
    };

export const useCheckoutThemeContext = () => {
    return useContext(CheckoutThemeContext);
};

export default CheckoutThemeContextProvider;
